import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import Lessons from './pages/Lessons';
import LessonView from './pages/LessonView';
import Games from './pages/Games';
import Review from './pages/Review';
import Progress from './pages/Progress';
import DifficultWords from './pages/DifficultWords';
import Dictionary from './pages/Dictionary';
import Login from './pages/Login';
import Register from './pages/Register';
import FlashcardQuiz from './games/FlashcardQuiz';
import ListeningChallenge from './games/ListeningChallenge';
import MatchingGame from './games/MatchingGame';
import SpellingGame from './games/SpellingGame';
import SentenceGame from './games/SentenceGame';
import AnagramGame from './games/AnagramGame';
import FillGapGame from './games/FillGapGame';
import DictationGame from './games/DictationGame';
import CustomSets from './pages/CustomSets';
import CustomSetEdit from './pages/CustomSetEdit';
import CustomSetAdd from './pages/CustomSetAdd';
import CustomSetLearn from './pages/CustomSetLearn';

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎓</div>
          <div>Ładowanie...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper (redirects to home if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Layout><Home /></Layout></ProtectedRoute>} />
      <Route path="/lessons" element={<ProtectedRoute><Layout><Lessons /></Layout></ProtectedRoute>} />
      <Route path="/lessons/:id" element={<ProtectedRoute><Layout><LessonView /></Layout></ProtectedRoute>} />
      <Route path="/games" element={<ProtectedRoute><Layout><Games /></Layout></ProtectedRoute>} />
      <Route path="/games/flashcard" element={<ProtectedRoute><Layout><FlashcardQuiz /></Layout></ProtectedRoute>} />
      <Route path="/games/listening" element={<ProtectedRoute><Layout><ListeningChallenge /></Layout></ProtectedRoute>} />
      <Route path="/games/matching" element={<ProtectedRoute><Layout><MatchingGame /></Layout></ProtectedRoute>} />
      <Route path="/games/spelling" element={<ProtectedRoute><Layout><SpellingGame /></Layout></ProtectedRoute>} />
      <Route path="/games/sentence" element={<ProtectedRoute><Layout><SentenceGame /></Layout></ProtectedRoute>} />
      <Route path="/games/anagram" element={<ProtectedRoute><Layout><AnagramGame /></Layout></ProtectedRoute>} />
      <Route path="/games/fillgap" element={<ProtectedRoute><Layout><FillGapGame /></Layout></ProtectedRoute>} />
      <Route path="/games/dictation" element={<ProtectedRoute><Layout><DictationGame /></Layout></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><Layout><Review /></Layout></ProtectedRoute>} />
      <Route path="/difficult" element={<ProtectedRoute><Layout><DifficultWords /></Layout></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Layout><Progress /></Layout></ProtectedRoute>} />
      <Route path="/dictionary" element={<ProtectedRoute><Layout><Dictionary /></Layout></ProtectedRoute>} />
      <Route path="/custom-sets" element={<ProtectedRoute><Layout><CustomSets /></Layout></ProtectedRoute>} />
      <Route path="/custom-sets/:id" element={<ProtectedRoute><Layout><CustomSetEdit /></Layout></ProtectedRoute>} />
      <Route path="/custom-sets/:id/add" element={<ProtectedRoute><Layout><CustomSetAdd /></Layout></ProtectedRoute>} />
      <Route path="/custom-sets/:id/learn" element={<ProtectedRoute><Layout><CustomSetLearn /></Layout></ProtectedRoute>} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;

